/**
 * The connection banner — SRS D5, docs/06-UI-SPEC.md §17.3.
 *
 * Nothing is shown for the first minute and a half of an outage, because nothing has
 * gone wrong: the pen still works and everything drawn is kept and merged when the
 * line comes back. What appears after that says so in as many words, so a teacher
 * mid-lesson knows to carry on rather than to stop and worry.
 */

import type { ConnectionNotice } from '../../board/connection.js';

export interface ConnectionBannerProps {
  readonly notice: ConnectionNotice;
  /** Try the connection again now. Only offered once it has been down a long time. */
  readonly onRetry: () => void;
}

const BANNER =
  'pointer-events-auto flex items-center gap-3 rounded-md px-4 py-2 text-base shadow-md';

export function ConnectionBanner({ notice, onRetry }: ConnectionBannerProps) {
  if (notice === 'none') return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none absolute left-1/2 top-4 z-10 -translate-x-1/2"
    >
      {notice === 'reconnecting' ? (
        <p className={`${BANNER} bg-warning text-text-inverse`}>Reconnecting… your work is safe</p>
      ) : (
        <p className={`${BANNER} bg-danger text-text-inverse`}>
          Disconnected. Your work is saved.
          <button
            type="button"
            onClick={onRetry}
            className="h-touch rounded-md bg-surface px-4 text-base font-medium text-text-primary transition duration-fast ease-out hover:bg-surface-hover"
          >
            Reconnect
          </button>
        </p>
      )}
    </div>
  );
}
